import { HUES, SHAPES, timing, variantForLevel, } from './params';
const FEEDBACK_MS = timing.feedbackMs;
function pickN(arr, n, seed = 0) {
    const pool = [...arr];
    const out = [];
    let s = seed || Math.floor(Math.random() * 1000);
    while (out.length < n && pool.length > 0) {
        s = (s * 1664525 + 1013904223) % 4294967296;
        const idx = s % pool.length;
        out.push(pool.splice(idx, 1)[0]);
    }
    return out;
}
function buildProgression(rules) {
    const shapeCycle = rules.includes('shape') ? pickN(SHAPES, 3) : [SHAPES[0], SHAPES[0], SHAPES[0]];
    const hueCycle = rules.includes('hue') ? pickN(HUES, 3) : [HUES[0], HUES[0], HUES[0]];
    const countCycle = rules.includes('count')
        ? [1, 2, 3]
        : [1, 1, 1];
    return { shapeCycle, hueCycle, countCycle };
}
export function buildStimulus(trialIdx, level) {
    const variant = variantForLevel(level);
    const N = variant.matrixSize;
    const prog = buildProgression(variant.rules);
    // Règle simple : shape varie par colonne, hue par ligne, count par diagonale.
    // En 2×2 : on simplifie — shape varie par colonne, hue par ligne.
    // Index row-major : [row * N + col]
    const cells = [];
    for (let r = 0; r < N; r += 1) {
        for (let c = 0; c < N; c += 1) {
            cells.push({
                shape: prog.shapeCycle[c % prog.shapeCycle.length],
                hue: prog.hueCycle[r % prog.hueCycle.length],
                count: prog.countCycle[(r + c) % prog.countCycle.length],
            });
        }
    }
    const correctIdx = N * N - 1;
    const correctCell = cells[correctIdx];
    const cellsHidden = cells.map((c, i) => (i === correctIdx ? null : c));
    // Génère candidats : correct + (nCandidates-1) distractors avec règles altérées.
    const distractors = [];
    while (distractors.length < variant.nCandidates - 1) {
        const altShape = SHAPES[Math.floor(Math.random() * SHAPES.length)];
        const altHue = HUES[Math.floor(Math.random() * HUES.length)];
        const altCount = [1, 2, 3][Math.floor(Math.random() * 3)];
        const candidate = { shape: altShape, hue: altHue, count: altCount };
        // Skip si identique à correct ou à distractor déjà présent.
        if (candidate.shape === correctCell.shape &&
            candidate.hue === correctCell.hue &&
            candidate.count === correctCell.count) {
            continue;
        }
        if (distractors.some((d) => d.shape === candidate.shape && d.hue === candidate.hue && d.count === candidate.count)) {
            continue;
        }
        distractors.push(candidate);
    }
    const correctId = `t${trialIdx}-correct`;
    const candidates = [
        { id: correctId, cell: correctCell },
        ...distractors.map((d, i) => ({ id: `t${trialIdx}-d${i}`, cell: d })),
    ].sort(() => Math.random() - 0.5);
    const ruleHints = [];
    if (variant.rules.includes('shape'))
        ruleHints.push('forme par colonne');
    if (variant.rules.includes('hue'))
        ruleHints.push('couleur par ligne');
    if (variant.rules.includes('count'))
        ruleHints.push('nombre par diagonale');
    const ruleHint = ruleHints.join(' · ');
    return {
        trialIdx,
        matrixSize: N,
        cells: cellsHidden,
        correctId,
        candidates,
        ruleHint,
    };
}
export function createMindBenderAdapter(level = 1) {
    let state = {
        subPhase: 'idle',
        stim: null,
        feedback: null,
        level,
        variant: variantForLevel(level),
    };
    const listeners = new Set();
    let resolver = null;
    let rejecter = null;
    let trialStart = 0;
    const timers = [];
    function clearTimers() {
        while (timers.length > 0) {
            const t = timers.pop();
            if (t !== undefined)
                clearTimeout(t);
        }
    }
    function update(patch) {
        state = { ...state, ...patch };
        for (const l of listeners) {
            try {
                l(state);
            }
            catch {
                /* ignored */
            }
        }
    }
    return {
        mount() { },
        unmount() {
            clearTimers();
            listeners.clear();
            if (rejecter) {
                rejecter(new Error('unmount'));
                rejecter = null;
                resolver = null;
            }
        },
        presentStimulus(stim) {
            clearTimers();
            return new Promise((resolve, reject) => {
                resolver = resolve;
                rejecter = reject;
                update({ subPhase: 'pre_matrix', stim, feedback: null });
                timers.push(setTimeout(() => {
                    update({ subPhase: 'awaiting_response' });
                    trialStart = Date.now();
                }, timing.preMatrixMs));
            });
        },
        async showFeedback(correct) {
            update({ subPhase: 'feedback', feedback: correct ? 'correct' : 'incorrect' });
            await new Promise((resolve) => {
                timers.push(setTimeout(() => {
                    update({ subPhase: 'iti' });
                    resolve();
                }, FEEDBACK_MS));
            });
        },
        pause() { },
        resume() { },
        abort() {
            clearTimers();
            if (rejecter) {
                rejecter(new Error('aborted'));
                rejecter = null;
                resolver = null;
            }
        },
        getState() {
            return state;
        },
        subscribe(listener) {
            listeners.add(listener);
            listener(state);
            return () => {
                listeners.delete(listener);
            };
        },
        handlePick(candidateId) {
            if (state.subPhase !== 'awaiting_response')
                return;
            const stim = state.stim;
            if (!stim)
                return;
            const correct = candidateId === stim.correctId;
            const rt = Date.now() - trialStart;
            const resp = { pickedId: candidateId, correct, reactionTimeMs: rt };
            if (resolver) {
                const r = resolver;
                resolver = null;
                rejecter = null;
                r(resp);
            }
        },
        setLevel(newLevel) {
            const v = variantForLevel(newLevel);
            update({ level: newLevel, variant: v });
        },
    };
}
//# sourceMappingURL=adapter.js.map